import { FC, useEffect, useState } from 'react';
import { Checkbox, InlineNotification, Modal } from '@carbon/react';
import style from './parties-overview.module.scss';

export interface ClearFlagAddress {
  id: number;
  label: string;
}

interface Props {
  open: boolean;
  addresses: ClearFlagAddress[];
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (selectedIds: number[]) => void;
}

export const ClearFlagsModal: FC<Props> = ({
  open,
  addresses,
  loading,
  error,
  onClose,
  onConfirm,
}) => {
  const [selection, setSelection] = useState<Set<number>>(
    () => new Set(addresses.map((a) => a.id))
  );

  // Re-initialise selection to all-selected each time the modal opens.
  useEffect(() => {
    if (open) setSelection(new Set(addresses.map((a) => a.id)));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal
      open={open}
      modalHeading="Clear Flags"
      primaryButtonText={loading ? 'Clearing…' : 'Clear Selected'}
      secondaryButtonText="Cancel"
      primaryButtonDisabled={loading || selection.size === 0}
      onRequestClose={onClose}
      onSecondarySubmit={onClose}
      onRequestSubmit={() => onConfirm(Array.from(selection))}
    >
      <p>
        Select which address flags to clear. Clearing a flag accepts the parsed
        address as-is and removes the review indicator.
      </p>
      <p className={style.modalCaution}>
        <strong>This action cannot be undone.</strong>
      </p>
      {addresses.length === 0 ? (
        <p>No flagged addresses found.</p>
      ) : (
        addresses.map(({ id, label }) => (
          <Checkbox
            key={id}
            id={`clear-flag-${id}`}
            labelText={label}
            checked={selection.has(id)}
            onChange={(_e, { checked }) =>
              setSelection((prev) => {
                const next = new Set(prev);
                if (checked) next.add(id);
                else next.delete(id);
                return next;
              })
            }
          />
        ))
      )}
      {error && (
        <div className={style.modalNotification}>
          <InlineNotification
            kind="error"
            lowContrast
            title="Error"
            subtitle={error}
            hideCloseButton
          />
        </div>
      )}
    </Modal>
  );
};
